import { ReferentialKey } from 'api/referential';
import { ComponentKey } from 'enums/componentKey';
import { InputsConfig } from 'types/form/inputsConfig';
import {
  BorrowerLoanFormFieldNames,
  BorrowerLoanFormValues,
  createDefaultTier,
  LoanTypeCodes
} from './borrowerLoanForm';

export const borrowerLoanInputsConfig: InputsConfig<
  Exclude<keyof BorrowerLoanFormValues, 'id'>
> = {
  [BorrowerLoanFormFieldNames.LoanAmount]: {
    componentKey: ComponentKey.InputWithUnit,
    name: BorrowerLoanFormFieldNames.LoanAmount,
    type: 'number',
    required: true,
    label: 'BORROWER_LOAN_FORM.LABELS.LOAN_AMOUNT',
    placeholder: 'BORROWER_LOAN_FORM.PLACEHOLDERS.LOAN_AMOUNT',
    unit: '€'
  },

  [BorrowerLoanFormFieldNames.isActive]: {
    componentKey: ComponentKey.Custom,
    name: BorrowerLoanFormFieldNames.isActive
  },

  // Rendu manuellement (FormikSwitch dans le layout) — entrée requise par le type.
  [BorrowerLoanFormFieldNames.TieredRate]: {
    componentKey: ComponentKey.Custom,
    name: BorrowerLoanFormFieldNames.TieredRate
  },

  [BorrowerLoanFormFieldNames.InsuranceAmount]: {
    componentKey: ComponentKey.InputWithUnit,
    name: BorrowerLoanFormFieldNames.InsuranceAmount,
    type: 'number',
    required: false,
    label: 'BORROWER_LOAN_FORM.LABELS.CURRENT_INSURANCE_AMOUNT',
    placeholder: 'BORROWER_LOAN_FORM.PLACEHOLDERS.CURRENT_INSURANCE_AMOUNT',
    unit: '€'
  },

  [BorrowerLoanFormFieldNames.LoanType]: {
    componentKey: ComponentKey.SelectCard,
    referentialKey: ReferentialKey.LoanType,
    name: BorrowerLoanFormFieldNames.LoanType,
    required: true,
    label: 'BORROWER_LOAN_FORM.LABELS.LOAN_TYPE',
    requireSelection: true,
    items: []
  },

  [BorrowerLoanFormFieldNames.InterestRateType]: {
    componentKey: ComponentKey.ReferentialDropdown,
    referentialKey: ReferentialKey.InterestRateType,
    name: BorrowerLoanFormFieldNames.InterestRateType,
    required: false,
    label: 'BORROWER_LOAN_FORM.LABELS.INTEREST_RATE_TYPE',
    placeholder: 'BORROWER_LOAN_FORM.PLACEHOLDERS.INTEREST_RATE_TYPE'
  },

  [BorrowerLoanFormFieldNames.DefermentType]: {
    componentKey: ComponentKey.ReferentialDropdown,
    referentialKey: ReferentialKey.DefermentType,
    name: BorrowerLoanFormFieldNames.DefermentType,
    required: false,
    label: 'BORROWER_LOAN_FORM.LABELS.DEFERMENT_TYPE',
    placeholder: 'BORROWER_LOAN_FORM.PLACEHOLDERS.DEFERMENT_TYPE'
  },

  [BorrowerLoanFormFieldNames.InterestRatePercentage]: {
    componentKey: ComponentKey.InputWithUnit,
    name: BorrowerLoanFormFieldNames.InterestRatePercentage,
    type: 'number',
    required: false,
    label: 'BORROWER_LOAN_FORM.LABELS.INTEREST_RATE_PERCENTAGE',
    placeholder: 'BORROWER_LOAN_FORM.PLACEHOLDERS.INTEREST_RATE_PERCENTAGE',
    unit: '%'
  },

  [BorrowerLoanFormFieldNames.TotalDuration]: {
    componentKey: ComponentKey.InputWithUnit,
    name: BorrowerLoanFormFieldNames.TotalDuration,
    type: 'number',
    required: false,
    label: 'BORROWER_LOAN_FORM.LABELS.TOTAL_DURATION',
    placeholder: 'BORROWER_LOAN_FORM.PLACEHOLDERS.TOTAL_DURATION',
    unit: 'Mois'
  },

  [BorrowerLoanFormFieldNames.DefermentPeriod]: {
    componentKey: ComponentKey.InputWithUnit,
    name: BorrowerLoanFormFieldNames.DefermentPeriod,
    type: 'number',
    required: false,
    label: 'BORROWER_LOAN_FORM.LABELS.DEFERMENT_PERIOD',
    placeholder: 'BORROWER_LOAN_FORM.PLACEHOLDERS.DEFERMENT_PERIOD',
    unit: 'Mois'
  },

  [BorrowerLoanFormFieldNames.RemainingDuration]: {
    componentKey: ComponentKey.InputWithUnit,
    name: BorrowerLoanFormFieldNames.RemainingDuration,
    type: 'number',
    required: false,
    label: 'BORROWER_LOAN_FORM.LABELS.REMAINING_DURATION',
    placeholder: 'BORROWER_LOAN_FORM.PLACEHOLDERS.REMAINING_DURATION',
    disabled: (state: BorrowerLoanFormValues) =>
      state[BorrowerLoanFormFieldNames.LoanType] !== LoanTypeCodes.CreditBail,
    unit: 'Mois'
  },

  [BorrowerLoanFormFieldNames.RentAmount]: {
    componentKey: ComponentKey.InputWithUnit,
    name: BorrowerLoanFormFieldNames.RentAmount,
    type: 'text',
    required: false,
    label: 'BORROWER_LOAN_FORM.LABELS.RENT_AMOUNT',
    placeholder: 'BORROWER_LOAN_FORM.PLACEHOLDERS.RENT_AMOUNT',
    disabled: (state: BorrowerLoanFormValues) =>
      state[BorrowerLoanFormFieldNames.LoanType] !== LoanTypeCodes.CreditBail,
    unit: '€'
  },

  [BorrowerLoanFormFieldNames.ResidualValue]: {
    componentKey: ComponentKey.InputText,
    name: BorrowerLoanFormFieldNames.ResidualValue,
    type: 'text',
    required: false,
    label: 'BORROWER_LOAN_FORM.LABELS.RESIDUAL_VALUE',
    placeholder: 'BORROWER_LOAN_FORM.PLACEHOLDERS.RESIDUAL_VALUE',
    disabled: (state: BorrowerLoanFormValues) =>
      state[BorrowerLoanFormFieldNames.LoanType] !== LoanTypeCodes.CreditBail
  },

  [BorrowerLoanFormFieldNames.Tiers]: {
    componentKey: ComponentKey.DynamicArray,
    name: BorrowerLoanFormFieldNames.Tiers,
    required: false,
    disabled: (state: BorrowerLoanFormValues) =>
      state[BorrowerLoanFormFieldNames.LoanType] !== LoanTypeCodes.Palier,
    fields: [
      {
        componentKey: ComponentKey.InputWithUnit,
        name: BorrowerLoanFormFieldNames.TierDuration,
        label: 'BORROWER_LOAN_FORM.LABELS.TIER_DURATION',
        type: 'number' as const,
        placeholder: 'BORROWER_LOAN_FORM.PLACEHOLDERS.TIER_DURATION',
        unit: 'Mois',
        colSpan: 2,
        required: true
      },
      {
        componentKey: ComponentKey.InputWithUnit,
        name: BorrowerLoanFormFieldNames.TierAmount,
        label: 'BORROWER_LOAN_FORM.LABELS.TIER_AMOUNT',
        type: 'text' as const,
        placeholder: 'BORROWER_LOAN_FORM.PLACEHOLDERS.TIER_AMOUNT',
        unit: '€',
        colSpan: 2,
        required: true
      },
      {
        componentKey: ComponentKey.InputWithUnit,
        name: BorrowerLoanFormFieldNames.TierRate,
        label: 'BORROWER_LOAN_FORM.LABELS.TIER_RATE',
        type: 'number' as const,
        placeholder: 'BORROWER_LOAN_FORM.PLACEHOLDERS.TIER_RATE',
        unit: '%',
        colSpan: 2,
        required: true
      }
    ],
    minItems: 2,
    maxItems: 10,
    gridCols: 6,
    compact: true,
    addButtonText: 'BORROWER_LOAN_FORM.BUTTONS.ADD_TIER',
    initialItem: createDefaultTier()
  }
};
